/**
 * analytics.ts — wrapper PostHog para o frontend Athena.
 *
 * Único ponto de chamada de analytics no frontend. Componentes e hooks
 * nunca chamam posthog.capture() diretamente — sempre usam este módulo.
 *
 * Comportamento noop automático quando VITE_POSTHOG_KEY não está definido
 * (dev sem chave, CI, preview).
 */

import posthog, { type PostHogInterface } from 'posthog-js';

// ---------------------------------------------------------------------------
// Catálogo canônico de eventos (espelha wiki/analytics/EVENTS.md)
// ---------------------------------------------------------------------------

const KNOWN_EVENTS = new Set([
  // Aquisição
  'landing_viewed',
  'landing_section_scrolled',
  'landing_cta_clicked',
  'landing_testimonials_viewed',
  'pricing_preview_viewed',
  'pricing_card_clicked',
  // Auth
  'signup_started',
  'signup_succeeded',
  'signup_failed',
  'login_succeeded',
  // Nota: falhas de auth via Google Sign-In não distinguem signup de login no
  // catch path (a info `isNewUser` só existe após o popup completar). Por isso
  // emitimos `signup_failed` para qualquer falha — não há `login_failed` em uso.
  'signup_completed',
  // Login bloqueado dentro de in-app browser (Instagram/Facebook/TikTok etc).
  // OAuth do Google não funciona em webview — orientamos abrir no navegador.
  'signup_blocked_webview',
  // Fluxo email+senha: login_failed distingue falha de auth do fluxo de senha
  // (diferente de signup_failed, que cobre qualquer falha no path de cadastro).
  // password_reset_requested, email_verification_sent: fluxo de recuperação/verificação.
  // account_link_prompted, account_link_succeeded: linking OAuth pendente (CASO 1 não-trusted).
  'login_failed',
  'password_reset_requested',
  'email_verification_sent',
  'account_link_prompted',
  'account_link_succeeded',
  // Login passwordless (Firebase Email Link). login_method_shown registra quais
  // métodos a tela exibiu (is_in_app + source) — degrau de funil que inclui
  // webview. email_link_sent/completed/error cobrem o ciclo do magic link.
  // Nunca carregam PII (sem e-mail).
  'login_method_shown',
  'email_link_sent',
  'email_link_completed',
  'email_link_error',
  // Onboarding
  'onboarding_step_viewed',
  'onboarding_completed',
  'onboarding_skipped',
  // Assinatura
  'subscription_wizard_opened',
  'subscription_wizard_step_viewed',
  'subscription_intent_switched',
  'coupon_validation_attempted',
  'coupon_validation_succeeded',
  'coupon_validation_failed',
  'plan_selected',
  'checkout_initiated',
  'checkout_returned',
  'topup_initiated',
  'topup_completed',
  // Billing backend (também disparados pelo back, mas listados para consistência)
  'payment_succeeded',
  'payment_failed',
  'subscription_created',
  'subscription_changed',
  'subscription_canceled',
  // Catálogo
  'catalog_viewed',
  'catalog_filter_applied',
  'simulation_card_clicked',
  'insufficient_credits_modal_shown',
  // Exam e tentativa
  'exam_started',
  'exam_question_answered',
  'exam_navigation',
  'exam_submission_attempted',
  // Escolha feita no dialog de envio: `choice: 'self' | 'ai'`. Mede quanta gente
  // opta pela autocorreção gratuita antes de encostar no paywall.
  'exam_submission_choice',
  'attempt_started',
  'attempt_submitted',
  // Grading SSE
  'grading_started',
  'grading_completed',
  'grading_failed',
  'correction_completed',
  'correction_failed',
  // Resultado
  'result_viewed',
  'result_tab_switched',
  'result_paywall_shown',
  'result_paywall_cta_clicked',
  'result_chat_message_sent',
  // Autocorreção (OAB 2ª fase): o aluno marca o espelho e vê a nota estimada
  // por ele mesmo. `self_grade_ai_cta_clicked` é o sinal de conversão da tela.
  'self_grade_started',
  'self_grade_completed',
  'self_grade_revised',
  'self_grade_ai_cta_clicked',
  // Retenção e perfil
  'account_viewed',
  'history_viewed',
  'competencies_viewed',
  'attempt_reopened',
  'subscription_canceled_self',
  // Exit-intent feedback (landing)
  'landing_exit_feedback_shown',
  'landing_exit_feedback_submitted',
  'landing_exit_feedback_dismissed',
  // Anúncios (tier gratuito). Existem para detectar DANO cedo, não para provar
  // receita: medir o ganho de conversão exigiria ~161 mil usuários, mas uma
  // queda causada pelo anúncio aparece com poucos milhares de eventos.
  // A decisão de consentimento é o denominador de todo o resto — sem ela não dá
  // para separar "não viu anúncio porque recusou" de "não viu porque não perguntamos".
  // Nenhuma propriedade carrega PII: `placement_path` é o pathname da rota com
  // os parâmetros dinâmicos normalizados para `:id`.
  'ads_consent_prompted',
  'ads_consent_decided',
  'ads_slot_rendered',
  'ads_slot_unfilled',
]);

// ---------------------------------------------------------------------------
// Estado interno
// ---------------------------------------------------------------------------

let _initialized = false;
let _ph: PostHogInterface | null = null;

function isEnabled(): boolean {
  return _initialized && _ph !== null;
}

// Fila local para eventos disparados antes de init() rodar (ex.: landing_viewed
// em Landing.tsx disparado antes de requestIdleCallback executar initAnalytics).
// Limite de 50 itens evita memory leak se a chave VITE_POSTHOG_KEY estiver ausente
// (init nunca roda, fila nunca drena — sem chave, descarte silencioso é correto).
type PendingEvent =
  | { kind: 'track'; args: [string, Record<string, unknown> | undefined] }
  | { kind: 'identify'; args: [string, Record<string, unknown> | undefined] }
  | { kind: 'reset'; args: [] }
  | { kind: 'setUserProperty'; args: [string, unknown] }
  | { kind: 'setUserPropertyOnce'; args: [string, unknown] }
  | { kind: 'setSuperProperty'; args: [string, unknown] }
  | { kind: 'setSuperPropertyOnce'; args: [string, unknown] };

const MAX_QUEUE_SIZE = 50;
let _pendingEvents: PendingEvent[] = [];

// ---------------------------------------------------------------------------
// API pública
// ---------------------------------------------------------------------------

/**
 * Inicializa o PostHog. Deve ser chamado uma vez em main.jsx antes do render.
 * Noop se VITE_POSTHOG_KEY não estiver definido.
 */
function init(): void {
  if (typeof window === 'undefined') return;

  const key = import.meta.env.VITE_POSTHOG_KEY as string | undefined;
  if (!key) {
    // Sem chave — wrapper permanece noop. Sem warnings ruidosos.
    return;
  }

  // Proxy reverso gerenciado (CNAME hermes.athena-sim.com -> proxyhog.com):
  // ingestão E script do recorder saem do nosso domínio, o que impede
  // bloqueador/ITP de derrubar evento e session replay. O fallback aponta para
  // o mesmo proxy de propósito — env ausente não deve silenciosamente voltar a
  // chamar us.i.posthog.com direto.
  const host =
    (import.meta.env.VITE_POSTHOG_HOST as string | undefined) ?? 'https://hermes.athena-sim.com';

  // Atribuir o singleton imediatamente — chamadas entre `init` e o callback
  // `loaded` (window de ~50–300ms enquanto o SDK termina de subir) ainda são
  // encaminhadas para a fila interna do posthog-js. Sem isso, eventos disparados
  // em mount muito cedo (ex.: landing_viewed em Landing.tsx) eram descartados
  // pelo guard `isEnabled()`.
  _ph = posthog as unknown as PostHogInterface;
  _initialized = true;

  // Drenar eventos enfileirados antes do init (gap entre mount do app e
  // requestIdleCallback executar initAnalytics — pode ser até ~2s).
  const pending = _pendingEvents;
  _pendingEvents = [];
  for (const ev of pending) {
    if (ev.kind === 'track') track(ev.args[0], ev.args[1]);
    else if (ev.kind === 'identify') identify(ev.args[0], ev.args[1]);
    else if (ev.kind === 'reset') reset();
    else if (ev.kind === 'setUserProperty') setUserProperty(ev.args[0], ev.args[1]);
    else if (ev.kind === 'setUserPropertyOnce') setUserPropertyOnce(ev.args[0], ev.args[1]);
    else if (ev.kind === 'setSuperProperty') setSuperProperty(ev.args[0], ev.args[1]);
    else if (ev.kind === 'setSuperPropertyOnce') setSuperPropertyOnce(ev.args[0], ev.args[1]);
  }

  posthog.init(key, {
    api_host: host,
    // `api_host` é o proxy, então o SDK não consegue derivar sozinho a URL do
    // app. Sem `ui_host`, os links do toolbar apontam para o domínio do proxy.
    ui_host: 'https://us.posthog.com',
    autocapture: true,
    capture_pageview: true,
    session_recording: {
      maskAllInputs: true,
      // Mascara textareas de resposta de simulado (PII de conteúdo)
      maskTextSelector: '[data-ph-mask], textarea',
    },
    loaded(ph) {
      _ph = ph;
    },
  });
}

/**
 * Identifica o usuário autenticado. Usa Firebase UID como distinctId.
 * Nunca envia PII (email, name, photoUrl).
 */
function identify(userId: string, props?: Record<string, unknown>): void {
  if (!isEnabled()) {
    if (_pendingEvents.length < MAX_QUEUE_SIZE) {
      _pendingEvents.push({ kind: 'identify', args: [userId, props] });
    }
    return;
  }
  _ph!.identify(userId, props);
}

/**
 * Reseta a identidade (chamado no logout). Novo anônimo na próxima sessão.
 */
function reset(): void {
  if (!isEnabled()) {
    if (_pendingEvents.length < MAX_QUEUE_SIZE) {
      _pendingEvents.push({ kind: 'reset', args: [] });
    }
    return;
  }
  _ph!.reset();
}

/**
 * Emite um evento de produto.
 * Em dev, avisa no console se o evento não está no catálogo canônico.
 */
function track(event: string, props?: Record<string, unknown>): void {
  if (!isEnabled()) {
    if (_pendingEvents.length < MAX_QUEUE_SIZE) {
      _pendingEvents.push({ kind: 'track', args: [event, props] });
    }
    return;
  }

  if (import.meta.env.DEV && !KNOWN_EVENTS.has(event)) {
    console.warn(`[analytics] Evento desconhecido: "${event}". Verifique EVENTS.md.`);
  }

  _ph!.capture(event, props);
}

/**
 * Define uma propriedade persistente do usuário (user property via identify).
 * Usa `set` (sobrescreve). Para first-touch use setUserPropertyOnce.
 */
function setUserProperty(key: string, value: unknown): void {
  if (!isEnabled()) {
    if (_pendingEvents.length < MAX_QUEUE_SIZE) {
      _pendingEvents.push({ kind: 'setUserProperty', args: [key, value] });
    }
    return;
  }
  _ph!.setPersonProperties({ [key]: value });
}

/**
 * Define propriedade do usuário somente na primeira vez (setOnce).
 * Ideal para first-touch UTMs e signup_date.
 */
function setUserPropertyOnce(key: string, value: unknown): void {
  if (!isEnabled()) {
    if (_pendingEvents.length < MAX_QUEUE_SIZE) {
      _pendingEvents.push({ kind: 'setUserPropertyOnce', args: [key, value] });
    }
    return;
  }
  _ph!.setPersonProperties({}, { [key]: value });
}

/**
 * Registra super property — adicionada a todos os eventos desta sessão.
 */
function setSuperProperty(key: string, value: unknown): void {
  if (!isEnabled()) {
    if (_pendingEvents.length < MAX_QUEUE_SIZE) {
      _pendingEvents.push({ kind: 'setSuperProperty', args: [key, value] });
    }
    return;
  }
  _ph!.register({ [key]: value });
}

/**
 * Registra super property somente se ainda não foi definida (registerOnce).
 */
function setSuperPropertyOnce(key: string, value: unknown): void {
  if (!isEnabled()) {
    if (_pendingEvents.length < MAX_QUEUE_SIZE) {
      _pendingEvents.push({ kind: 'setSuperPropertyOnce', args: [key, value] });
    }
    return;
  }
  _ph!.register_once({ [key]: value });
}

/**
 * Para gravação de sessão (usar em rotas /admin/*).
 */
function stopSessionRecording(): void {
  if (!isEnabled()) return;
  _ph!.stopSessionRecording();
}

/**
 * Retoma gravação de sessão.
 */
function startSessionRecording(): void {
  if (!isEnabled()) return;
  _ph!.startSessionRecording();
}

/**
 * Flush e shutdown — raramente necessário no frontend.
 */
async function shutdown(): Promise<void> {
  if (!isEnabled()) return;
  // posthog-js não expõe shutdown; reset é suficiente para limpar estado.
  _ph!.reset();
}

const analytics = {
  init,
  identify,
  reset,
  track,
  setUserProperty,
  setUserPropertyOnce,
  setSuperProperty,
  setSuperPropertyOnce,
  stopSessionRecording,
  startSessionRecording,
  shutdown,
};

export default analytics;
